import { useState, useEffect, useMemo } from 'react'
import { X, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface DrawerItem {
  id: string
  name: string
  description: string
  thumbnailUrl?: string
  color?: string
}

interface AddItemsDrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  items: DrawerItem[]
  currentItemIds?: string[]
  onAddItems: (itemIds: string[]) => void
  searchPlaceholder?: string
  addButtonLabel?: string
  emptyMessage?: string
  emptySearchMessage?: string
}

export function AddItemsDrawer({
  isOpen,
  onClose,
  title,
  items,
  currentItemIds = [],
  onAddItems,
  searchPlaceholder = 'Search...',
  addButtonLabel = 'Add Selected',
  emptyMessage = 'No items available',
  emptySearchMessage = 'No items match your search',
}: AddItemsDrawerProps) {
  const [searchText, setSearchText] = useState('')
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())

  // Filter items based on search text
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) {
      return items
    }

    const lowerSearch = searchText.toLowerCase()
    return items.filter(
      item =>
        item.name.toLowerCase().includes(lowerSearch) ||
        item.description.toLowerCase().includes(lowerSearch)
    )
  }, [searchText, items])

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText('')
      setSelectedItemIds(new Set())
    }
  }, [isOpen])

  const handleToggleItem = (itemId: string) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  const handleClearSelection = () => {
    setSelectedItemIds(new Set())
  }

  const handleAddItems = () => {
    if (selectedItemIds.size === 0) return

    onAddItems(Array.from(selectedItemIds))
    setSelectedItemIds(new Set())
    onClose()
  }

  const isItemAlreadyAdded = (itemId: string) => currentItemIds.includes(itemId)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed z-50 flex flex-col bg-white shadow-2xl',
          // Mobile: bottom drawer
          'max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:h-[85vh] max-sm:rounded-t-2xl',
          // Desktop: right drawer
          'sm:inset-y-0 sm:right-0 sm:top-0 sm:h-full sm:w-[420px]'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Header */}
        <div className="flex h-14 flex-shrink-0 items-center justify-between border-b px-4">
          <h2 id="drawer-title" className="text-lg font-semibold text-black">
            {title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-[#0033A0] hover:bg-[#0033A0]/10"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="flex-shrink-0 border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666666]" />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="pl-9"
              aria-label={searchPlaceholder}
            />
          </div>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredItems.length === 0 ? (
            <div className="flex min-h-[200px] items-center justify-center text-center">
              <div>
                <p className="text-sm text-[#666666]">
                  {searchText ? emptySearchMessage : emptyMessage}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map(item => {
                const isSelected = selectedItemIds.has(item.id)
                const isAlreadyAdded = isItemAlreadyAdded(item.id)

                return (
                  <button
                    key={item.id}
                    onClick={() => !isAlreadyAdded && handleToggleItem(item.id)}
                    disabled={isAlreadyAdded}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
                      isAlreadyAdded
                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                        : isSelected
                          ? 'border-[#0033A0] bg-[#0033A0]/5'
                          : 'border-gray-200 hover:border-[#0033A0]/50 hover:bg-gray-50'
                    )}
                  >
                    {/* Checkbox */}
                    <div
                      className={cn(
                        'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2',
                        isAlreadyAdded
                          ? 'border-gray-300 bg-gray-200'
                          : isSelected
                            ? 'border-[#0033A0] bg-[#0033A0]'
                            : 'border-gray-300'
                      )}
                    >
                      {(isSelected || isAlreadyAdded) && (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Thumbnail */}
                    {item.thumbnailUrl && (
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                        <img
                          src={item.thumbnailUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={e => {
                            e.currentTarget.src = '/move-placeholder.png'
                          }}
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <h4 className="line-clamp-1 font-medium text-black">
                        {item.name}
                      </h4>
                      <p className="mt-0.5 line-clamp-2 text-sm text-[#666666]">
                        {item.description}
                      </p>
                      {isAlreadyAdded && (
                        <p className="mt-1 text-xs text-[#666666]">
                          Already added
                        </p>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-[#666666]">
              {selectedItemIds.size} selected
            </span>
            {selectedItemIds.size > 0 && (
              <button
                onClick={handleClearSelection}
                className="text-sm text-[#0033A0] hover:underline"
              >
                Clear selection
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleAddItems}
              disabled={selectedItemIds.size === 0}
              className="flex-1 bg-[#0033A0] hover:bg-[#0033A0]/90"
            >
              {addButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
